<template>
  <h5>Alta de campaña</h5>

  <Form
    role="form"
    class="text-start mt-4 qs-alta-campana"
    :validation-schema="schema"
    @submit="handleAltaCampana"
  >
    <soft-field
      type="text"
      id="nombre"
      v-model="campana.nombre"
      :useLabel="true"
      label="Nombre"
      name="nombre"
      class="mb-2 label-input2"
      largo="80"
    />

    <!-- TIPSTER: Admin (1) y Publicista (5) -->
   <soft-select
        v-if="[1,5].includes(Number(miRol))"
        id="tipsterid"
        v-model="tipsterid"
        name="tipsterid"
        :valores="tipsters"
        :useLabel="true"
        label="Tipster"
        class="mb-2 label-input2 label-select2"
    />

    <soft-select
        v-if="(Number(miRol) === 2) || (Number(miRol) === 4) || (tipsterid > 0)"
        id="canalid"
        v-model="campana.canalid"
        name="canalid"
        :valores="canales"
        :useLabel="true"
        label="Canal"
        class="mb-2 label-input2 label-select2"
    />
    <div class="q-form-footer">
      <button
        type="button"
        class="btn-q-secondary"
        @click="$emit('cerrar-modal')"
      >
        Volver
      </button>

      <button
        type="submit"
        class="btn-q-primary"
        :disabled="loading || !puedeConfirmar"
      >
        Confirmar
      </button>
    </div>
  </Form>
</template>

<script>
import SoftField from "@/components/SoftField.vue";
import SoftSelect from "@/components/SoftSelect.vue";

import showSwal from "/src/mixins/showSwal.js";
import { Form } from "vee-validate";
import * as yup from "yup";
import { mapGetters } from "vuex";

export default {
  name: "AltaCampanaForm",
  components: { SoftField, SoftSelect, Form },
  props: { campanaBotName: String },
  emits: ["cerrar-modal"],

  data() {
    const schema = yup.object().shape({
      nombre: yup
        .string()
        .required("El nombre es necesario!")
        .max(80, "El tamaño máximo es de 80 caracteres"),
    });

    return {
      loading: false,
      schema,
      tipsterid: 0,
      tipsters: [],
      canales: [],
      campana: {
        nombre: "",
        tipsterid: "",
        canalid: null,
      },
    };
  },

  computed: {
    ...mapGetters("auth", ["rolEfectivo"]),
    miRol() { return this.rolEfectivo; },
    muestraSelectTipster() {
      // Admin y Publicista
      return [1, 5].includes(this.miRol);
    },
    muestraSelectCanal() {
      // Si no hay select tipster (tipster/tipster-admin), siempre mostramos canal.
      // Si hay select tipster, solo cuando ya eligió uno.
      return !this.muestraSelectTipster || Number(this.tipsterid) > 0;
    },
    puedeConfirmar() {
      // Para evitar "crear sin canal"
      return !!this.campana.nombre?.trim() && Number(this.campana.canalid) > 0;
    },
  },

  watch: {
    async tipsterid(newVal, oldVal) {
      if (Number(newVal) === Number(oldVal)) return;

      // Reset canal al cambiar tipster (evita canal "fantasma")
      this.campana.canalid = null;

      // Si aún no eligió tipster, no cargamos canales
      if (this.muestraSelectTipster && Number(newVal) <= 0) {
        this.canales = [];
        return;
      }

      await this.obtenerCanales();
    },
  },

  created() {
    const miTipsterId = Number(localStorage.getItem("usuarioTipsterId") || 0);
    if ([2, 4].includes(this.miRol)) {
      this.tipsterid = miTipsterId;
    }
  },

  methods: {
    async obtenerCanales() {
      const rol = Number(this.miRol);

      // Tipster/AdminTipster: a veces backend usa el tipster del token,
      // pero mandamos ambos nombres por compatibilidad.
      const payload =
        [2, 4].includes(rol)
          ? {}
          : { tipster_id: Number(this.tipsterid), tipsterId: Number(this.tipsterid) };

      const resp = await this.$store.dispatch("auth/campanasCanales", payload);

      // Por si backend devuelve {data:[...]} o directamente [...]
      this.canales = Array.isArray(resp) ? resp : (resp?.data ?? []);
    },


    async handleAltaCampana() {
      this.campana.tipsterid = Number(this.tipsterid) || 0;

      if (!this.puedeConfirmar) {
        showSwal.methods.showSwal({
          type: "error",
          message: "Debes indicar nombre y seleccionar un canal.",
          width: 500,
        });
        return;
      }

      this.loading = true;
      try {
        const resp = await this.$store.dispatch("auth/guardaCampana", this.campana);
        this.loading = false;

        if (resp.res === "OK") {
          showSwal.methods.showSwal({
            type: "success",
            message: "Se ha registrado la nueva campaña",
            width: 600,
            timer: 10000,
          });
          this.$emit("campana-creada", resp.id);
        } else {
          showSwal.methods.showSwal({
            type: "error",
            message: resp.mensaje,
            width: 500,
          });
        }
      } catch (error) {
        this.loading = false;
        showSwal.methods.showSwal({
          type: "error",
          message: "Se ha producido un error",
          width: 500,
        });
      }
    },
  },

    async mounted() {
      const rol = Number(this.miRol);

      try {
        // ✅ Tipster/AdminTipster: solo cargar canales de su tipster (NO listaTipsters)
        if ([2, 4].includes(rol)) {
          this.tipsters = [];
          await this.obtenerCanales();
          return;
        }

        // ✅ Publicista: carga tipsters permitidos, sin canales hasta elegir
        if (rol === 5) {
          this.tipsters = await this.$store.dispatch("auth/campanasTipsters");
          this.tipsterid = 0;
          this.canales = [];
          return;
        }

        // ✅ Admin: lista completa tipsters
        this.tipsters = await this.$store.dispatch("auth/listaTipsters");
        if (Number(this.tipsterid) > 0) await this.obtenerCanales();
      } catch (e) {
        console.error("AltaCampana mounted error:", e);
        this.canales = [];
      }
    },



};
</script>

<style scoped>
/* =========================
   Botones Quanty
   ========================= */
.btn-q-primary{
  background: var(--q-gold);
  color: var(--q-gold-on);
  border:none;
  border-radius:10px;
  height:48px;
  padding:0 24px;
  font-weight:600;
  font-size:1rem;
  cursor:pointer;
  transition:0.2s ease;
}
.btn-q-primary:hover{
  background: var(--q-gold-hover);
  transform: translateY(-2px);
}

.btn-q-secondary{
  background: var(--q-hover);
  color: var(--q-muted);
  border:none;
  border-radius:10px;
  height:48px;
  padding:0 24px;
  font-weight:600;
  font-size:1rem;
  cursor:pointer;
  transition:0.2s ease;
}
.btn-q-secondary:hover{
  background: var(--q-border-md);
  transform: translateY(-2px);
}

/* =========================
   Footer del formulario
   ========================= */
:deep(form){
  width:100%;
  max-width:100%;
}

.q-form-footer{
  width:100%;
  box-sizing:border-box;

  display:flex;
  justify-content: space-between; /* Volver izq, Confirmar der */
  align-items:center;
  gap:14px;

  padding-top:16px;
  margin-top:18px;
  border-top:1px solid var(--q-border);
}

/* Botones dentro del footer */
.q-form-footer .btn-q-primary,
.q-form-footer .btn-q-secondary{
  flex:0 0 auto;
  min-width:180px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
}

/* =============================================================
   AltaCampana: label + control en la misma línea (select)
   usando el wrapper interno de SoftSelect: .label-select2 > div
============================================================= */
:deep(.qs-alta-campana .label-select2 > div){
  display:flex;
  align-items:center;
  gap:16px;
}

/* label con ancho fijo */
:deep(.qs-alta-campana .label-select2 > div .form-label){
  margin:0;
  flex: 0 0 120px;
  min-width:120px;
}

/* select ocupa el resto */
:deep(.qs-alta-campana .label-select2 > div .form-control){
  flex: 1 1 auto;
  width:100%;
  min-width:0;
}

/* Móvil: apilar selects y botones a ancho completo */
@media (max-width:768px){
  :deep(.qs-alta-campana .label-select2 > div){
    flex-direction:column;
    align-items:stretch;
    gap:6px;
  }
  :deep(.qs-alta-campana .label-select2 > div .form-label){
    flex: 0 0 auto;
    min-width:0;
  }
  .q-form-footer .btn-q-primary,
  .q-form-footer .btn-q-secondary{
    width:100%;
    min-width:unset;
  }
}
</style>
